"use client";

import { StarIcon } from "@heroicons/react/24/solid";
import { ComponentPropsWithoutRef, FC } from "react";
import { useLocale, useMessages } from "../context/i18n";
import { rivalPage as enRivalPage } from "../dictionaries/en/rival-page";

// One review-platform badge. The `logoSrc` points at a public asset in
// `/public/svg/logos/` (we ship the wordmark for G2 and Capterra today —
// both available locally so this never makes a network request at render
// time). `rating` is an open scale out of 5 (we render 4.8 with a clipped
// star overlay so half/three-quarter ratings still read correctly).
//
// The badge is styled for the dark "try it yourself" section of Alternative:
// translucent surface, white text, amber stars, and the platform wordmark
// flipped to monochrome white via `brightness-0 invert` so the G2 orange /
// Capterra multi-color marks don't read as random colored chips on the
// slate-950 background.
export type ReviewBadge = {
  platform: string;
  rating: number;
  href: string;
  logoSrc: string;
  logoAlt: string;
};

type AlternativeReviewBadgesProps = {
  badges: ReviewBadge[];
} & ComponentPropsWithoutRef<"div">;

const AlternativeReviewBadges: FC<AlternativeReviewBadgesProps> = ({
  badges,
  ...rest
}) => {
  const m = useMessages("rivalPage", enRivalPage);
  const lang = useLocale();
  return (
    <div
      {...rest}
      className={`${rest.className ?? ""} flex flex-wrap items-stretch justify-center gap-3`}
    >
      {badges.map((badge) => {
        const rating = formatRating(badge.rating, lang);
        const withRating = (text: string) =>
          text
            .replace("{rating}", rating)
            .replace("{platform}", badge.platform);
        return (
          <a
            key={badge.platform}
            href={badge.href}
            target="_blank"
            rel="noopener nofollow"
            aria-label={withRating(m.seeRating)}
            className="group flex items-center gap-3 rounded-xl bg-white/[0.04] px-4 py-2.5 text-white ring-1 ring-white/10 backdrop-blur-sm transition hover:bg-white/[0.08] hover:ring-white/20"
          >
            <img
              src={badge.logoSrc}
              alt={badge.logoAlt}
              width={64}
              height={22}
              className="h-5 w-auto shrink-0 opacity-80 brightness-0 invert transition group-hover:opacity-100"
              loading="lazy"
            />
            <span
              aria-hidden="true"
              className="h-6 w-px shrink-0 bg-white/15"
            />
            <div className="flex flex-col items-start leading-tight">
              <StarRow rating={badge.rating} label={withRating(m.rated)} />
              <span className="mt-0.5 text-xs font-semibold tracking-wide text-white/90">
                {rating}
                <span className="text-white/50">{" / 5"}</span>
              </span>
            </div>
          </a>
        );
      })}
    </div>
  );
};

function formatRating(rating: number, lang: string): string {
  return rating.toLocaleString(lang, { minimumFractionDigits: 1 });
}

// Two layered rows of solid star icons: a faint white/15 base, plus an
// amber overlay clipped horizontally to `(rating / 5) * 100%`. Solid
// stars (not outline) read more clearly on the dark background than the
// stroked outline variants. The outer `aria-label` carries the textual
// rating for screen readers.
const StarRow: FC<{ rating: number; label: string }> = ({ rating, label }) => {
  const widthPercent = Math.max(0, Math.min(5, rating)) * 20;
  return (
    <span role="img" aria-label={label} className="relative inline-flex">
      <span className="flex text-white/15">
        {[0, 1, 2, 3, 4].map((i) => (
          <StarIcon key={i} className="h-4 w-4" aria-hidden="true" />
        ))}
      </span>
      <span
        className="absolute inset-0 flex overflow-hidden text-amber-400"
        style={{ width: `${widthPercent}%` }}
        aria-hidden="true"
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <StarIcon key={i} className="h-4 w-4 shrink-0" />
        ))}
      </span>
    </span>
  );
};

export default AlternativeReviewBadges;
